import React, { useEffect, useRef, useState} from "react";
import {
    Button,
    Grid,
    Paper,
    Typography,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import PropTypes from 'prop-types';

// =============================================
// EMAIL VALIDATION UTILITY
// =============================================
export function validateEmails({ studentEmail, professorEmail }) {
  const errors = {};
  const emailRE = /^\S+@\S+\.\S+$/;

  if (!studentEmail || !String(studentEmail).trim()) {
    errors.studentEmail = 'Student email is required';
  } else if (!emailRE.test(String(studentEmail).trim())) {
    errors.studentEmail = 'Student email is invalid';
  }

  if (!professorEmail || !String(professorEmail).trim()) {
    errors.professorEmail = 'Professor email is required';
  } else if (!emailRE.test(String(professorEmail).trim())) {
    errors.professorEmail = 'Professor email is invalid';
  }

  return errors;
}

// =============================================
// EMAIL TRANSCRIPT DIALOG COMPONENT
// =============================================
const EmailTranscriptDialog = React.memo((props) => {
    const {
        showEmailModal,
        setShowEmailModal,
        setEmailStatus,
        handleSendTranscript,
        selectedChatbot,
        chatbotConfigs,
        ssoStudentEmail
    } = props;

    const [studentName, setStudentName] = useState('');
    const [professorEmail, setProfessorEmail] = useState('');
    const [professorName, setProfessorName] = useState('');
    const [extraNote, setExtraNote] = useState('');
    const [studentEmail, setStudentEmail] = useState('');
    const statusRef = useRef(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (showEmailModal) {
            // The student's name isn't reliably available from SSO (some IdPs map the
            // "name" claim to the email/UPN), so leave it as a plain editable field
            // remembered only via localStorage. The email field is still driven by
            // the authenticated SSO identity, which is reliable and verified.
            const storedStudentName = localStorage.getItem('studentName');
            const storedStudentEmail = localStorage.getItem('studentEmail');

            const resolvedStudentEmail = ssoStudentEmail || storedStudentEmail || '';

            setStudentEmail(resolvedStudentEmail);
            setStudentName(storedStudentName || '');

            if (resolvedStudentEmail) {
                localStorage.setItem('studentEmail', resolvedStudentEmail);
            }

            setEmailStatus('');
            setErrors({});
        }
    }, [showEmailModal, setEmailStatus, ssoStudentEmail]);

    // useEffect to scroll when emailStatus changes so student sees the status message
    useEffect(() => {
        if (props.emailStatus && statusRef.current) {
            statusRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' }); // Use 'block: end' to ensure it's at the bottom
        }
    }, [props.emailStatus]);


    const currentChatbotName = selectedChatbot && chatbotConfigs[selectedChatbot]
        ? chatbotConfigs[selectedChatbot].name
        : 'General Chatbot';

    const defaultEmailMessage = `
Hi ${professorName || '...'},

Here is ${studentName || 'Student Name'}'s transcript for chatbot ${currentChatbotName} on ${new Date().toLocaleString()}.

Thanks
`;

    return (
        <Dialog open={showEmailModal} onClose={() => { setShowEmailModal(false); setEmailStatus(''); }} maxWidth="sm" fullWidth>
            <DialogTitle>Send Transcript</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            margin="dense"
                            id="studentName"
                            label="Your Name (for the email)"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={studentName}
                            onChange={(e) => {
                                setStudentName(e.target.value);
                                localStorage.setItem('studentName', e.target.value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            margin="dense"
                            id="studentEmail"
                            label="Your Email"
                            type="email"
                            fullWidth
                            variant="outlined"
                            value={studentEmail}
                            onChange={(e) => {
                                setStudentEmail(e.target.value);
                                localStorage.setItem('studentEmail', e.target.value);
                            }}
                            required
                            disabled={!!ssoStudentEmail}
                            error={!!errors.studentEmail}
                            helperText={errors.studentEmail || (ssoStudentEmail ? 'Provided by your login and cannot be changed here.' : undefined)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            margin="dense"
                            id="professorName"
                            label="Instructor's Name (Include Prefix if needed)"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={professorName}
                            onChange={(e) => setProfessorName(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            margin="dense"
                            id="professorEmail"
                            label="Instructor's Email"
                            type="email"
                            fullWidth
                            variant="outlined"
                            value={professorEmail}
                            onChange={(e) => setProfessorEmail(e.target.value)}
                            required
                            error={!!errors.professorEmail}
                            helperText={errors.professorEmail}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom>
                            Default Message:
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'background.default', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                            {defaultEmailMessage}
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            margin="dense"
                            id="extraNote"
                            label="Extra Note (Optional)"
                            type="text"
                            fullWidth
                            multiline
                            rows={4}
                            variant="outlined"
                            value={extraNote}
                            onChange={(e) => setExtraNote(e.target.value)}
                            placeholder="Add any additional notes for your instructor here..."
                        />
                    </Grid>
                </Grid>
                {props.emailStatus && (
                    <Typography
                        ref={statusRef}
                        variant="h6"
                        sx={{
                            mt: 2,
                            fontSize: '2.0rem',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            color: props.emailStatus.includes('Failed') ? 'error.main' : 'success.main'
                        }}
                    >
                        {props.emailStatus}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => { setShowEmailModal(false); setEmailStatus(''); }}>Close</Button>
                <Button
                    onClick={() => {
                        const newErrors = validateEmails({ studentEmail, professorEmail });
                        if (Object.keys(newErrors).length) {
                            setErrors(newErrors);
                            return;
                        }
                        setErrors({});
                        console.log('Sending transcript with:', { studentName, studentEmail, professorName, professorEmail, extraNote });
                        handleSendTranscript(studentName, studentEmail, professorName, professorEmail, extraNote);
                    }}
                    variant="contained"
                    color="primary"
                >
                    Send Email
                </Button>
            </DialogActions>
        </Dialog>
    );
});

EmailTranscriptDialog.propTypes = {
    showEmailModal: PropTypes.bool.isRequired,
    setShowEmailModal: PropTypes.func.isRequired,
    emailStatus: PropTypes.string.isRequired,
    setEmailStatus: PropTypes.func.isRequired,
    handleSendTranscript: PropTypes.func.isRequired,
    selectedChatbot: PropTypes.string.isRequired,
    chatbotConfigs: PropTypes.object.isRequired,
    ssoStudentEmail: PropTypes.string,
};

export { EmailTranscriptDialog };